import React, { useState, useEffect } from "react";
import Select from "react-select";
import { COLORS } from "../../Constants/theme";

function CustomDropdown({
  value,
  name,
  handleChange,
  placeholder,
  options,
  handleGetOptionValue,
  handleGetOptionName,
  isDisabled = false,
  customClasses,
  error,
  touched,
  label,
  required = false,
  helpText,
}) {
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (value) {
      setIsFocused(true);
    } else {
      setIsFocused(false);
    }
  }, [value]);

  // Styles aligned with CustomDatePicker, with highlight when value is selected
  const customStyles = {
    control: (base, state) => ({
      ...base,
      borderColor:
        error && touched
          ? COLORS.status.error
          : isDisabled
            ? "#6b7280" // gray-500 when disabled
            : state.isFocused || isFocused
              ? "#4f46e5" // indigo-500 when focused or selecting
              : "#d1d5db", // gray-300 default
      borderWidth: state.isFocused || isFocused ? "2px" : "1px",
      borderRadius: "0.5rem",
      minHeight: "46px",
      backgroundColor: isDisabled ? "#f3f4f6" : "#ffffff", // gray-100 for disabled
      opacity: isDisabled ? 0.7 : 1,
      cursor: isDisabled ? "not-allowed" : "default",
      boxShadow: "none",
      transition: "all 0.2s ease",
      "&:hover": {
        borderColor:
          isDisabled
            ? "#6b7280" // gray-500 on hover if disabled
            : error && touched
              ? COLORS.status.error
              : "#4f46e5", // indigo-500
      },
      padding: "0 8px",
    }),
    singleValue: (base) => ({
      ...base,
      color: isDisabled ? "#6b7280" : "#1f2937", // gray-500 when disabled, gray-800 otherwise
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: isDisabled ? "#6b7280" : "#6b7280", // consistent gray-500
      "&:hover": {
        color: isDisabled ? "#6b7280" : "#4f46e5", // indigo-500 hover
      },
    }),
    // Keep these consistent
    valueContainer: (base) => ({
      ...base,
      padding: "8px 12px",
    }),
    input: (base) => ({
      ...base,
      margin: "0",
      padding: "0",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#9ca3af",
    }),
    clearIndicator: (base) => ({
      ...base,
      color: "#6b7280",
      "&:hover": {
        color: COLORS.status.error,
      },
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      marginTop: "4px",
      borderRadius: "0.5rem",
      border: "1px solid #e5e7eb",
      boxShadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      overflow: "hidden",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#4f46e5"
        : state.isFocused
          ? "#e0e7ff"
          : "#ffffff",
      color: state.isSelected ? "#ffffff" : "#374151",
      padding: "10px 16px",
      cursor: "pointer",
      transition: "background-color 0.2s ease",
      "&:hover": {
        backgroundColor: state.isSelected ? "#4f46e5" : "#e0e7ff",
      },
    }),
  };
  

  return (
    <div className="mb-4 relative w-full">
      {label && (
        <label
          htmlFor={name}
          className="block text-sm font-medium text-gray-700 mb-1 text-left"
        >
          {label}
          {required && <span className="text-rose-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative w-full">
        {placeholder && !label && (
          <label
            className={`absolute left-3 transition-all z-10 bg-white px-1 text-left ${
              isFocused || value
                ? "top-[-8px] text-xs text-gray-700"
                : "top-1/2 text-gray-500 text-sm -translate-y-1/2"
            }`}
          >
            {placeholder}
            {required && !label && <span className="text-rose-500 ml-1">*</span>}
          </label>
        )}

        <Select
          inputId={name}
          value={value}
          name={name}
          onChange={(selectedOption) => {
            handleChange(selectedOption);
            setIsFocused(!!selectedOption);
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            if (!value) setIsFocused(false);
          }}
          className={customClasses}
          classNamePrefix="react-select"
          options={options || []}
          getOptionValue={handleGetOptionValue}
          getOptionLabel={handleGetOptionName}
          isDisabled={isDisabled}
          placeholder={label ? placeholder : ""}
          menuPortalTarget={document.body}
          styles={customStyles}
        />
      </div>

      {helpText && (
        <p className="mt-1 text-xs text-gray-500">{helpText}</p>
      )}

      {error && touched && (
        <p className="mt-1 text-sm text-rose-500">{error}</p>
      )}
    </div>
  );
}

export default CustomDropdown;